import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AttributeValue } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteAttributeValue } from '../hooks/use-attribute-mutations';

export interface AttributeValueDeleteDialogProps {
  value: AttributeValue | null;
  onOpenChange: (open: boolean) => void;
}

/** The backend refuses (409 RESOURCE_IN_USE) while a product still uses the value. */
export function AttributeValueDeleteDialog({
  value,
  onOpenChange,
}: AttributeValueDeleteDialogProps) {
  const { t } = useTranslation(['attributes', 'common', 'errors']);
  const remove = useDeleteAttributeValue();
  const message = useErrorMessage();
  const error = isApiError(remove.error) ? remove.error : null;

  return (
    <AlertDialog
      open={value !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('attributes:deleteValue.title', { label: value?.label ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('attributes:deleteValue.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {error?.code === 'RESOURCE_IN_USE'
                ? t('attributes:deleteValue.inUse')
                : message(remove.error)}
              {error?.requestId ? (
                <span className="block text-xs">
                  {t('errors:requestIdShort', { requestId: error.requestId })}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (value) {
                remove.mutate(
                  { attributeId: value.attributeId, valueId: value.id },
                  { onSuccess: () => onOpenChange(false) },
                );
              }
            }}
          >
            {remove.isPending
              ? t('attributes:deleteValue.submitting')
              : t('attributes:deleteValue.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
